import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal, ModalBody } from 'reactstrap';
import { Redirect } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StorefrontIcon from '@mui/icons-material/Storefront';

import PickUpPointsAddOrUpdate from './PickUpPointsAddOrUpdate';
import { showNotification, successNofication } from 'src/shared/components/Notification';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import { ERROR_MESSAGES, PAGE_TIMEOUT } from 'src/utils/pnd/PndConstants';

const PickUpPointsModal = ({ modalShow, setModalShow, isTitle, dataInfo, onRefresh }) => {
  const [isRedirect, setIsRedirect] = useState(false);
  const [isMapData, setMapData] = useState(null);
  const [isLatitude, setLatitude] = useState(null);
  const [isLongitude, setLongitide] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = () => {
    if (!loading) {
      setModalShow(prevState => !prevState);
    }
  };

  if (isRedirect) {
    window.location.reload();
    return <Redirect to="/web_app/pnd_pick_up_point/view" />;
  }

  const handleSubmit = async formValues => {
    try {
      setLoading(true);

      const resolvedAddress =
        isMapData ||
        formValues?.collectionPointAddress ||
        formValues?.address ||
        dataInfo?.collectionPointAddress;

      if (!resolvedAddress || !resolvedAddress.trim()) {
        showNotification(
          'theme-light',
          'ltr',
          'SuccessCode: 400',
          'Message: Please specify a valid Pick Up Point Address.',
        );
        setLoading(false);
        return;
      }

      formValues['merchantInfo'] = {
        merchantId: localStorage.getItem('mi'),
      };
      formValues['collectionPointAddress'] = resolvedAddress;
      formValues['latitude'] = isLatitude !== null ? isLatitude : (formValues?.latitude || dataInfo?.latitude);
      formValues['longitude'] = isLongitude !== null ? isLongitude : (formValues?.longitude || dataInfo?.longitude);

      if (dataInfo?.pickUpPointPointId) {
        formValues['pickUpPointPointId'] = dataInfo.pickUpPointPointId;
      }

      const response = await PndRestApi.AddOrUpdateMerchantPickUpPoint(formValues);
      if (response && response.status === 200 && response.data?.status === 200) {
        successNofication(
          'theme-light',
          'ltr',
          `SuccessCode: ${response.data.status}`,
          ERROR_MESSAGES.successMessage,
        );
        setTimeout(() => {
          if (onRefresh) {
            onRefresh();
          }
          setIsRedirect(true);
        }, PAGE_TIMEOUT);
      } else {
        showNotification(
          'theme-light',
          'ltr',
          `SuccessCode: ${response?.data?.status || 500}`,
          `Message: ${response?.data?.message || 'Failed to save Pick Up Point'}`,
        );
      }
    } catch (error) {
      console.error('Error saving pick up point:', error);
      showNotification(
        'theme-light',
        'ltr',
        'SuccessCode: 500',
        ERROR_MESSAGES.tryCatchError,
      );
    } finally {
      setLoading(false);
    }
  };

  const modalHeading = isTitle || (dataInfo ? 'Edit - Pick Up Point Details' : 'Add - Pick Up Point Details');

  return (
    <div>
      <Modal
        isOpen={modalShow}
        toggle={toggle}
        modalClassName="ltr-support"
        className="theme-light ltr-support"
        size="lg"
        backdrop={loading ? 'static' : true}
        style={{ maxWidth: '850px' }}
      >
        {/* Modern Modal Header */}
        <div
          className="position-relative"
          style={{
            background: 'linear-gradient(135deg, #32CA73 0%, #1e8e4f 100%)',
            padding: '1.25rem 1.75rem',
            borderRadius: '0.5rem 0.5rem 0 0',
            boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
          }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div
                className="d-flex align-items-center justify-content-center me-3"
                style={{
                  width: '46px',
                  height: '46px',
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '12px',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <StorefrontIcon style={{ fontSize: '26px', color: '#fff' }} />
              </div>
              <div>
                <h4
                  className="m-0 text-white fw-bold"
                  style={{
                    fontSize: '1.35rem',
                    letterSpacing: '0.3px',
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  {modalHeading}
                </h4>
                <p className="m-0 text-white-50 small">
                  {dataInfo
                    ? 'Update collection point location, contacts, and coordinates'
                    : 'Register a new collection point location, contacts, and coordinates'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={toggle}
              disabled={loading}
              className="btn-close-custom"
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                opacity: loading ? 0.5 : 1,
              }}
              onMouseEnter={e => {
                if (!loading) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                  e.currentTarget.style.transform = 'rotate(90deg)';
                }
              }}
              onMouseLeave={e => {
                if (!loading) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                  e.currentTarget.style.transform = 'rotate(0deg)';
                }
              }}
            >
              <CloseIcon style={{ color: '#fff', fontSize: '20px' }} />
            </button>
          </div>
        </div>

        {/* Modal Body with loading overlay */}
        <ModalBody className="p-0" style={{ background: '#f8fafc' }}>
          {loading && (
            <div
              className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
              style={{
                background: 'rgba(255,255,255,0.85)',
                zIndex: 1050,
                backdropFilter: 'blur(4px)',
                top: 0,
                left: 0,
              }}
            >
              <div className="text-center">
                <CircularProgress size={45} style={{ color: '#32CA73' }} />
                <p className="mt-2 text-muted fw-bold small">Saving Details...</p>
              </div>
            </div>
          )}

          <div style={{ maxHeight: 'calc(90vh - 120px)', overflowY: 'auto' }}>
            <PickUpPointsAddOrUpdate
              onSubmit={handleSubmit}
              setLatitude={setLatitude}
              setLongitide={setLongitide}
              address={dataInfo?.collectionPointAddress}
              setMapData={setMapData}
              dataInfo={dataInfo}
              onCancel={toggle}
            />
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

PickUpPointsModal.propTypes = {
  modalShow: PropTypes.bool.isRequired,
  setModalShow: PropTypes.func.isRequired,
  isTitle: PropTypes.string,
  dataInfo: PropTypes.shape(),
  onRefresh: PropTypes.func,
};

export default connect(state => ({
  rtl: state.rtl,
}))(PickUpPointsModal);
